import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  Phone, MessageCircle, Calendar, DollarSign, ChevronLeft, ChevronRight,
  Gauge, Fuel, Settings, Car, Hash, Palette, Shield, BadgeCheck, 
  CheckCircle, MapPin, Clock, Star, X, ZoomIn, Share2, Heart,
  Calculator, CreditCard, FileText, Award, Zap, Thermometer, Users
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import useSEO from '@/hooks/useSEO';
import ConsentCheckboxes from '@/components/ConsentCheckboxes';
import { Slider } from '@/components/ui/slider';

const VehicleDetailPage = () => {
  const { stockNumber, id } = useParams();
  const navigate = useNavigate();
  const vehicleId = stockNumber || id;
  
  const [vehicle, setVehicle] = useState(null);
  const [similarVehicles, setSimilarVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showTestDrive, setShowTestDrive] = useState(false);
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  
  // Financing calculator state
  const [downPayment, setDownPayment] = useState(2000);
  const [loanTerm, setLoanTerm] = useState(60);
  const [creditScore, setCreditScore] = useState('good'); // good, fair, rebuilding
  
  // Form states
  const [testDriveForm, setTestDriveForm] = useState({
    full_name: '', email: '', phone: '', preferred_date: '', preferred_time: '', message: ''
  });
  const [requestInfoForm, setRequestInfoForm] = useState({
    full_name: '', email: '', phone: '', message: ''
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [nonMarketingConsent, setNonMarketingConsent] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ map_lat: 30.166498, map_lng: -97.769819, map_zoom: 15 });

  useSEO('vehicle', vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model} | We Have Your Car` : 'Vehicle Details');

  useEffect(() => {
    fetchVehicle();
    const handleScroll = () => setIsSticky(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const [vehicleRes, allVehiclesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/vehicles/${vehicleId}`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/site-settings`).catch(() => null)
      ]);
      setVehicle(vehicleRes.data);
      if (settingsRes?.data) setSiteSettings(prev => ({ ...prev, ...settingsRes.data }));
      
      // Get similar vehicles (same make or body type)
      const similar = allVehiclesRes.data
        .filter(v => v.id !== vehicleRes.data.id && 
          (v.make === vehicleRes.data.make || v.body_type === vehicleRes.data.body_type))
        .slice(0, 6);
      setSimilarVehicles(similar);
    } catch (error) {
      toast.error('Vehicle not found');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  const formatMileage = (mileage) => new Intl.NumberFormat('en-US').format(mileage);

  // Calculate monthly payment
  // Total = Car Price + $370 doc fee + 6.25% tax
  // APR: Good 10%, Fair 15%, Rebuilding 24%
  const calculateMonthlyPayment = () => {
    if (!vehicle) return 0;
    
    const docFee = 370;
    const taxRate = 0.0625;
    const basePrice = vehicle.price;
    const totalPrice = basePrice + docFee + (basePrice * taxRate);
    
    const rates = { good: 0.10, fair: 0.15, rebuilding: 0.24 };
    const annualRate = rates[creditScore] || 0.10;
    const monthlyRate = annualRate / 12;
    
    const principal = totalPrice - downPayment;
    if (principal <= 0) return 0;
    
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, loanTerm)) / (Math.pow(1 + monthlyRate, loanTerm) - 1);
    return Math.round(payment);
  };
  
  // Calculate total financed amount for display
  const calculateTotalPrice = () => {
    if (!vehicle) return 0;
    const docFee = 370;
    const taxRate = 0.0625;
    return vehicle.price + docFee + (vehicle.price * taxRate);
  };

  const handleTestDriveSubmit = async (e) => {
    e.preventDefault();
    if (!testDriveForm.full_name || !testDriveForm.email || !testDriveForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/test-drives`, {
        ...testDriveForm, vehicle_id: vehicleId, marketing_consent: marketingConsent, non_marketing_consent: nonMarketingConsent
      });
      toast.success('Test drive scheduled! We\'ll confirm soon.');
      setShowTestDrive(false);
      setTestDriveForm({ full_name: '', email: '', phone: '', preferred_date: '', preferred_time: '', message: '' });
    } catch (error) {
      toast.error('Failed to schedule. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestInfoSubmit = async (e) => {
    e.preventDefault();
    if (!requestInfoForm.full_name || !requestInfoForm.email || !requestInfoForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/request-info`, {
        ...requestInfoForm, vehicle_id: vehicleId, source: 'vehicle_detail', marketing_consent: marketingConsent, non_marketing_consent: nonMarketingConsent
      });
      toast.success('Request sent! We\'ll contact you soon.');
      setShowRequestInfo(false);
    } catch (error) {
      toast.error('Failed to send request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b35]"></div>
      </div>
    );
  }

  if (!vehicle) return null;

  const images = vehicle.images?.length > 0 ? vehicle.images : ['/placeholder-car.jpg'];
  const monthlyPayment = calculateMonthlyPayment();

  // Trust badges based on vehicle data
  const trustBadges = [
    { icon: Shield, label: 'Clean Title', show: true },
    { icon: BadgeCheck, label: vehicle.carfax_file ? 'CARFAX Available' : 'History Available', show: true },
    { icon: CreditCard, label: 'Financing Available', show: true },
    { icon: CheckCircle, label: 'Inspected', show: true },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="vehicle-detail-page">
      {/* STICKY HEADER - Mobile */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 md:hidden ${isSticky ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="bg-[#132238]/95 backdrop-blur-md border-b border-[#1a2d47] px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#ff6b35] font-bold text-xl">{formatPrice(vehicle.price)}</p>
              <p className="text-[#a8b8d0] text-xs">{vehicle.year} {vehicle.make} {vehicle.model}</p>
            </div>
            <div className="flex gap-2">
              <a href="tel:5126660016" className="bg-[#22c55e] text-white p-2.5 rounded-full" data-testid="sticky-call-btn">
                <Phone size={18} />
              </a>
              <a href={`sms:5126660016?body=${encodeURIComponent(`I'm interested in the ${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`} className="bg-[#ff6b35] text-white p-2.5 rounded-full" data-testid="sticky-text-btn">
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4">
          {/* Image Gallery */}
          <div className="relative aspect-[4/3] md:aspect-[16/9] max-h-[70vh] bg-[#132238] rounded-lg overflow-hidden">
            <img 
              src={images[currentImageIndex]} 
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              className="w-full h-full object-cover"
              loading="eager"
            />
            
            {/* Image Counter */}
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
              {currentImageIndex + 1} / {images.length}
            </div>
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                  data-testid="next-image-btn"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            
            {/* Fullscreen Button */}
            <button 
              onClick={() => setShowFullscreen(true)}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
              data-testid="fullscreen-btn"
            >
              <ZoomIn size={20} />
            </button>
          </div>

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="flex gap-2 py-3 overflow-x-auto scrollbar-hide">
              {images.slice(0, 10).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === idx ? 'border-[#ff6b35]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
              {images.length > 10 && (
                <button 
                  onClick={() => setShowFullscreen(true)}
                  className="shrink-0 w-16 h-12 rounded-lg bg-[#1a2d47] flex items-center justify-center text-white text-xs font-medium"
                >
                  +{images.length - 10}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Vehicle Title & Price */}
        <section className="space-y-3">
          <h1 className="text-2xl md:text-4xl font-bold text-white font-['Exo_2']" data-testid="vehicle-title">
            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-3xl md:text-4xl font-bold text-[#ff6b35]" data-testid="vehicle-price">
              {formatPrice(vehicle.price)}
            </span>
            <span className="bg-[#1a2d47] text-[#a8b8d0] px-3 py-1 rounded-full text-sm">
              {formatMileage(vehicle.mileage)} mi
            </span>
            {vehicle.mpg_city > 0 && (
              <span className="bg-[#22c55e]/20 text-[#22c55e] px-3 py-1 rounded-full text-sm">
                {vehicle.mpg_city}/{vehicle.mpg_highway} MPG
              </span>
            )}
          </div>
        </section>

        {/* PRIMARY CTAs */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="primary-ctas">
          <a href="tel:5126660016" className="flex items-center justify-center gap-2 bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold py-3.5 px-4 rounded-xl transition-all">
            <Phone size={20} /> Call Now
          </a>
          <a href={`sms:5126660016?body=${encodeURIComponent(`I'm interested in the ${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`} className="flex items-center justify-center gap-2 bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold py-3.5 px-4 rounded-xl transition-all">
            <MessageCircle size={20} /> Text Us
          </a>
          <button onClick={() => setShowTestDrive(true)} className="flex items-center justify-center gap-2 bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-semibold py-3.5 px-4 rounded-xl transition-all" data-testid="schedule-test-drive-btn">
            <Calendar size={20} /> Test Drive / Check Availability
          </button>
          <Link to="/credit-application" className="flex items-center justify-center gap-2 bg-[#1a2d47] hover:bg-[#243b5a] text-white font-semibold py-3.5 px-4 rounded-xl border border-[#ff6b35] transition-all" data-testid="apply-financing-btn">
            <DollarSign size={20} /> Apply Financing
          </Link>
        </section>

        {/* TRUST & URGENCY BAR */}
        <section className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="trust-bar">
          {trustBadges.filter(b => b.show).map((badge, idx) => (
            <div key={idx} className="shrink-0 flex items-center gap-2 bg-[#132238] border border-[#1a2d47] px-4 py-2.5 rounded-full">
              <badge.icon size={16} className="text-[#22c55e]" />
              <span className="text-white text-sm font-medium whitespace-nowrap">{badge.label}</span>
            </div>
          ))}
        </section>

        {/* QUICK SPECS GRID (Bento Style) */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="specs-grid">
          {[
            { icon: Gauge, label: 'Mileage', value: `${formatMileage(vehicle.mileage)} mi` },
            { icon: Settings, label: 'Engine', value: vehicle.engine || 'N/A' },
            { icon: Car, label: 'Transmission', value: vehicle.transmission || 'Auto' },
            { icon: Zap, label: 'Drivetrain', value: vehicle.drivetrain || 'FWD' },
            { icon: Fuel, label: 'Fuel', value: vehicle.fuel_type || 'Gas' },
            { icon: Car, label: 'Body', value: vehicle.body_type || 'Sedan' },
          ].map((spec, idx) => (
            <div key={idx} className="bg-[#132238] border border-[#1a2d47] rounded-xl p-4 text-center">
              <spec.icon size={20} className="text-[#ff6b35] mx-auto mb-2" />
              <p className="text-[#a8b8d0] text-xs mb-1">{spec.label}</p>
              <p className="text-white font-semibold text-sm">{spec.value}</p>
            </div>
          ))}
        </section>

        {/* VIN & Stock Number */}
        {vehicle.vin && (
          <div className="flex flex-wrap gap-4 text-sm text-[#a8b8d0]" data-testid="vehicle-vin-section">
            <span><span className="text-[#64748b]">VIN:</span> <span className="text-white font-mono">{vehicle.vin}</span></span>
            <span><span className="text-[#64748b]">Stock #:</span> <span className="text-white">{vehicle.stock_number}</span></span>
          </div>
        )}

        {/* Vehicle History - Accidents, Owners, Use Type */}
        {(vehicle.accidents || vehicle.previous_owners || vehicle.use_type) && (
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="vehicle-history-section">
            {vehicle.accidents && (
              <div className="bg-[#132238] border border-[#1a2d47] rounded-xl p-4 flex items-center gap-3">
                <Shield size={20} className={vehicle.accidents.startsWith('0') || vehicle.accidents.toLowerCase().includes('no') ? 'text-[#22c55e]' : 'text-[#ffc107]'} />
                <div>
                  <p className="text-[#a8b8d0] text-xs">Accidents</p>
                  <p className="text-white font-semibold text-sm">{vehicle.accidents.startsWith('0') ? 'No Accidents' : vehicle.accidents}</p>
                </div>
              </div>
            )}
            {vehicle.previous_owners && (
              <div className="bg-[#132238] border border-[#1a2d47] rounded-xl p-4 flex items-center gap-3">
                <Users size={20} className="text-[#1a4d8c]" />
                <div>
                  <p className="text-[#a8b8d0] text-xs">Previous Owners</p>
                  <p className="text-white font-semibold text-sm">{vehicle.previous_owners}</p>
                </div>
              </div>
            )}
            {vehicle.use_type && (
              <div className="bg-[#132238] border border-[#1a2d47] rounded-xl p-4 flex items-center gap-3">
                <Car size={20} className="text-[#ff6b35]" />
                <div>
                  <p className="text-[#a8b8d0] text-xs">Use Type</p>
                  <p className="text-white font-semibold text-sm">{vehicle.use_type}</p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* FINANCING CALCULATOR - Only show for vehicles over $5000 */}
        {vehicle.price > 5000 && (
        <section className="bg-gradient-to-br from-[#132238] to-[#1a2d47] border border-[#1a2d47] rounded-2xl p-6" data-testid="financing-calculator">
          <div className="flex items-center gap-2 mb-4">
            <Calculator size={24} className="text-[#ff6b35]" />
            <h2 className="text-xl font-bold text-white">Estimate Your Payment</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Down Payment */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Down Payment</label>
              <Slider 
                min={0} 
                max={Math.min(vehicle.price * 0.5, 15000)} 
                step={500}
                value={[downPayment]}
                onValueChange={(val) => setDownPayment(val[0])}
                className="w-full [&_[role=slider]]:bg-[#ff6b35] [&_[role=slider]]:border-[#ff6b35] [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_span:first-child_span]:bg-[#ff6b35]"
                data-testid="down-payment-slider"
              />
              <p className="text-white font-semibold mt-2">{formatPrice(downPayment)}</p>
            </div>
            
            {/* Loan Term */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Loan Term</label>
              <select 
                value={loanTerm} 
                onChange={(e) => setLoanTerm(Number(e.target.value))}
                className="w-full bg-[#0a1628] border border-[#1a2d47] text-white rounded-lg px-3 py-2"
              >
                <option value="36">36 months</option>
                <option value="48">48 months</option>
                <option value="60">60 months</option>
                <option value="72">72 months</option>
                <option value="84">84 months</option>
              </select>
            </div>
            
            {/* Credit Score */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Credit Score</label>
              <select 
                value={creditScore} 
                onChange={(e) => setCreditScore(e.target.value)}
                className="w-full bg-[#0a1628] border border-[#1a2d47] text-white rounded-lg px-3 py-2"
              >
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="rebuilding">Rebuilding</option>
              </select>
            </div>
          </div>
          
          <div className="bg-[#0a1628] rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-[#a8b8d0] text-sm">Estimated Monthly Payment</p>
              <p className="text-4xl font-bold text-[#ff6b35]">{formatPrice(monthlyPayment)}<span className="text-lg text-[#a8b8d0]">/mo</span></p>
            </div>
            <Link to="/credit-application" className="w-full md:w-auto bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-bold py-3 px-8 rounded-full transition-all text-center" data-testid="get-preapproved-btn">
              Get Pre-Approved
            </Link>
          </div>
          <p className="text-[#64748b] text-xs mt-3 text-center">*Estimated payment includes tax & fees. Actual terms may vary based on credit approval.</p>
        </section>
        )}

        {/* FEATURES LIST */}
        {vehicle.features && vehicle.features.length > 0 && (
          <section className="bg-[#132238] border border-[#1a2d47] rounded-2xl p-6" data-testid="features-section">
            <h2 className="text-xl font-bold text-white mb-4">Features & Options</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {vehicle.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#a8b8d0]">
                  <CheckCircle size={16} className="text-[#22c55e] shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* VEHICLE DESCRIPTION */}
        {(vehicle.ai_description || vehicle.description) && (
          <section className="bg-[#132238] border border-[#1a2d47] rounded-2xl p-6" data-testid="description-section">
            <h2 className="text-xl font-bold text-white mb-4">About This Vehicle</h2>
            <p className="text-[#a8b8d0] leading-relaxed">{vehicle.ai_description || vehicle.description}</p>
          </section>
        )}

        {/* LEAD CAPTURE STRIP (Repeated CTAs) */}
        <section className="bg-gradient-to-r from-[#ff6b35] to-[#e55a2b] rounded-2xl p-6" data-testid="lead-capture-strip">
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-white">Ready to Make This Yours?</h2>
            <p className="text-white/80">Get in touch now - no obligation!</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <a href="tel:5126660016" className="flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 text-white py-3 rounded-xl transition-all">
              <Phone size={24} />
              <span className="text-sm font-medium">Call</span>
            </a>
            <a href={`sms:5126660016?body=${encodeURIComponent(`I'm interested in the ${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`} className="flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 text-white py-3 rounded-xl transition-all">
              <MessageCircle size={24} />
              <span className="text-sm font-medium">Text</span>
            </a>
            <button onClick={() => setShowTestDrive(true)} className="flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 text-white py-3 rounded-xl transition-all">
              <Calendar size={24} />
              <span className="text-sm font-medium">Schedule</span>
            </button>
          </div>
        </section>

        {/* DEALER TRUST SECTION */}
        <section className="bg-[#132238] border border-[#1a2d47] rounded-2xl p-6" data-testid="dealer-trust-section">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-3">We Have Your Car</h2>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="text-[#ffc107] fill-[#ffc107]" />
                ))}
                <span className="text-white ml-2">4.8</span>
                <span className="text-[#a8b8d0] ml-1">(50+ reviews)</span>
              </div>
              <div className="space-y-2 text-[#a8b8d0]">
                <p className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#ff6b35]" />
                  8203 Bluff Springs Rd, Unit 4, Austin, TX 78744
                </p>
                <p className="flex items-center gap-2">
                  <Phone size={16} className="text-[#ff6b35]" />
                  <a href="tel:5126660016" className="hover:text-white">(512) 666-0016</a>
                </p>
                <p className="flex items-center gap-2">
                  <Clock size={16} className="text-[#ff6b35]" />
                  Mon-Sat: 10AM - 6PM | Sun: Closed
                </p>
              </div>
            </div>
            <div className="md:w-64">
              <iframe 
                src={`https://maps.google.com/maps?q=${siteSettings.map_lat},${siteSettings.map_lng}&z=${siteSettings.map_zoom || 15}&output=embed`}
                className="w-full h-40 rounded-xl"
                loading="lazy"
                title="Dealer Location"
              />
            </div>
          </div>
        </section>

        {/* SIMILAR VEHICLES */}
        {similarVehicles.length > 0 && (
          <section data-testid="similar-vehicles">
            <h2 className="text-xl font-bold text-white mb-4">Similar Vehicles</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {similarVehicles.slice(0, 6).map((v) => (
                <Link 
                  key={v.id} 
                  to={`/inventory/${v.stock_number}`}
                  className="bg-[#132238] border border-[#1a2d47] rounded-xl overflow-hidden hover:border-[#ff6b35] transition-all group"
                >
                  <div className="aspect-[4/3] bg-[#1a2d47]">
                    <img 
                      src={v.images?.[0] || '/placeholder-car.jpg'} 
                      alt={`${v.year} ${v.make} ${v.model}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-white font-semibold text-sm truncate">{v.year} {v.make} {v.model}</p>
                    <p className="text-[#ff6b35] font-bold">{formatPrice(v.price)}</p>
                    <p className="text-[#a8b8d0] text-xs">{formatMileage(v.mileage)} mi</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* SEO CONTENT (Below Fold) */}
        <section className="border-t border-[#1a2d47] pt-8 mt-8" data-testid="seo-content">
          <div className="prose prose-invert max-w-none text-[#64748b] text-sm">
            <h3 className="text-white text-lg font-semibold mb-3">
              {vehicle.year} {vehicle.make} {vehicle.model} for Sale in Austin, TX
            </h3>
            <p>
              Looking for a quality {vehicle.year} {vehicle.make} {vehicle.model} in Austin, Texas? 
              We Have Your Car offers this exceptional {vehicle.body_type?.toLowerCase() || 'vehicle'} with 
              {vehicle.mileage ? ` ${formatMileage(vehicle.mileage)} miles` : ' low mileage'} at a competitive price of {formatPrice(vehicle.price)}. 
              {vehicle.engine && ` Powered by a ${vehicle.engine} engine,`} this {vehicle.make} delivers 
              {vehicle.mpg_city > 0 ? ` impressive fuel economy of ${vehicle.mpg_city} MPG city and ${vehicle.mpg_highway} MPG highway` : ' reliable performance'}.
            </p>
            <p>
              At We Have Your Car, located at 8203 Bluff Springs Rd in Austin, TX, we specialize in 
              quality pre-owned vehicles with flexible financing options for all credit types. 
              Our team is dedicated to helping you find the perfect vehicle to fit your needs and budget.
              Visit us Monday through Saturday, 10AM to 6PM, or call (512) 666-0016 to schedule your test drive today.
            </p>
            <p>
              We offer easy financing, competitive trade-in values, and a hassle-free buying experience. 
              Whether you're looking for a {vehicle.make} {vehicle.model} or exploring other options in our inventory, 
              our knowledgeable staff is here to help you every step of the way.
            </p>
          </div>
        </section>
      </div>

      {/* FULLSCREEN GALLERY MODAL */}
      <Dialog open={showFullscreen} onOpenChange={setShowFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none">
          <button 
            onClick={() => setShowFullscreen(false)}
            className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full"
          >
            <X size={24} />
          </button>
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            <img 
              src={images[currentImageIndex]} 
              alt=""
              className="max-w-full max-h-full object-contain"
            />
            {images.length > 1 && (
              <>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                  className="absolute left-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full"
                >
                  <ChevronLeft size={32} />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                  className="absolute right-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}
          </div>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 overflow-x-auto px-4">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 ${currentImageIndex === idx ? 'border-[#ff6b35]' : 'border-transparent opacity-60'}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* SCHEDULE TEST DRIVE / REQUEST INFORMATION MODAL */}
      <Dialog open={showTestDrive} onOpenChange={setShowTestDrive}>
        <DialogContent className="sm:max-w-md bg-[#132238] border-[#1a2d47] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Schedule Test Drive / Request Information</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTestDriveSubmit} className="space-y-4">
            <input type="text" placeholder="Full Name *" value={testDriveForm.full_name} onChange={(e) => setTestDriveForm(f => ({...f, full_name: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <input type="email" placeholder="Email *" value={testDriveForm.email} onChange={(e) => setTestDriveForm(f => ({...f, email: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <input type="tel" placeholder="Phone *" value={testDriveForm.phone} onChange={(e) => setTestDriveForm(f => ({...f, phone: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <input 
                  type="date" 
                  value={testDriveForm.preferred_date} 
                  onChange={(e) => setTestDriveForm(f => ({...f, preferred_date: e.target.value}))} 
                  className={`w-full min-w-0 bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white [color-scheme:dark] appearance-none ${!testDriveForm.preferred_date ? 'text-transparent' : ''}`}
                />
                {!testDriveForm.preferred_date && (
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] pointer-events-none">Date (Optional)</span>
                )}
              </div>
              <select value={testDriveForm.preferred_time} onChange={(e) => setTestDriveForm(f => ({...f, preferred_time: e.target.value}))} className="w-full min-w-0 bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white [color-scheme:dark]">
                <option value="">Time (Optional)</option>
                <option value="10:00 AM">10:00 AM</option>
                <option value="11:00 AM">11:00 AM</option>
                <option value="12:00 PM">12:00 PM</option>
                <option value="1:00 PM">1:00 PM</option>
                <option value="2:00 PM">2:00 PM</option>
                <option value="3:00 PM">3:00 PM</option>
                <option value="4:00 PM">4:00 PM</option>
                <option value="5:00 PM">5:00 PM</option>
              </select>
            </div>
            <textarea placeholder="Message (Optional) - Ask about availability, questions, etc." value={testDriveForm.message} onChange={(e) => setTestDriveForm(f => ({...f, message: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white h-20 resize-none" />
            <ConsentCheckboxes
              marketingConsent={marketingConsent}
              setMarketingConsent={setMarketingConsent}
              nonMarketingConsent={nonMarketingConsent}
              setNonMarketingConsent={setNonMarketingConsent}
            />
            <button type="submit" disabled={submitting} className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-bold py-3 rounded-xl disabled:opacity-50" data-testid="test-drive-submit-btn">
              {submitting ? 'Sending...' : 'Submit Request'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* REQUEST INFO MODAL */}
      <Dialog open={showRequestInfo} onOpenChange={setShowRequestInfo}>
        <DialogContent className="sm:max-w-md bg-[#132238] border-[#1a2d47] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Request Information</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequestInfoSubmit} className="space-y-4">
            <input type="text" placeholder="Full Name *" value={requestInfoForm.full_name} onChange={(e) => setRequestInfoForm(f => ({...f, full_name: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <input type="email" placeholder="Email *" value={requestInfoForm.email} onChange={(e) => setRequestInfoForm(f => ({...f, email: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <input type="tel" placeholder="Phone *" value={requestInfoForm.phone} onChange={(e) => setRequestInfoForm(f => ({...f, phone: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white" required />
            <textarea placeholder="Message (optional)" value={requestInfoForm.message} onChange={(e) => setRequestInfoForm(f => ({...f, message: e.target.value}))} className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-3 text-white h-24 resize-none" />
            <ConsentCheckboxes
              marketingConsent={marketingConsent}
              setMarketingConsent={setMarketingConsent}
              nonMarketingConsent={nonMarketingConsent}
              setNonMarketingConsent={setNonMarketingConsent}
            />
            <button type="submit" disabled={submitting} className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-bold py-3 rounded-xl disabled:opacity-50" data-testid="request-info-submit-btn">
              {submitting ? 'Sending...' : 'Send Request'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleDetailPage;
